import React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import AppLayout from "./components/layout/AppLayout";
import MobileSidebar from "./components/layout/MobileSidebar";
import { ScrollToTop } from "./components/layout/ScrollToTop";

import { useRole } from "@/hooks/useRole";
import { HeroBackgroundWrapper } from "./components/layout/HeroBackgroundWrapper";
import { CookieConsent } from "./components/ui/CookieConsent";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import AuthPage from "./pages/Auth";
import MentoresPage from "./pages/MentoresPage";
import JogosPage from "./pages/JogosPage";
import MentoriasPage from "./pages/MentoriasPage";
import GuiasPage from "./pages/GuiasPage";
import MentorPublicPage from "./pages/MentorPublicPage";
import ComunidadesPage from "./pages/ComunidadesPage";
import CommunityPage from "./pages/CommunityPage";
import AdminPanel from "./pages/AdminPanel";
import MentorPanel from "./pages/MentorPanel";
import PlayerPanel from "./pages/PlayerPanel";
import ComoFuncionaPage from "./pages/ComoFuncionaPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCanceled from "./pages/PaymentCanceled";
import DigitalContentSuccess from "./pages/DigitalContentSuccess";
import ChatPage from "./pages/ChatPage";
import AuthCallback from '@/pages/AuthCallback';
import NicknameDebug from './pages/debug/NicknameDebug';
import CommunityDebug from './pages/debug/CommunityDebug';
import DigitalContentPurchaseDebug from './components/debug/DigitalContentPurchaseDebug';
import EmailDebugPage from "./pages/EmailDebugPage";
import EmailTemplateTestPage from "./pages/EmailTemplateTestPage";
import { TermsOfService } from "./pages/TermsOfService";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { MentorGuide } from "./pages/MentorGuide";
import { PaymentPolicies } from "./pages/PaymentPolicies";
import ComoJogarValorant from "./pages/games/ComoJogarValorant";
import ComoJogarPokemonTCG from "./pages/games/ComoJogarPokemonTCG";
import ComoJogarFortnite from "./pages/games/ComoJogarFortnite";
import MentorValorant from "./pages/mentors/MentorValorant";
import MentorPokemonTCG from "./pages/mentors/MentorPokemonTCG";
import MentorFortnite from "./pages/mentors/MentorFortnite";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // Desabilita refetch automático ao focar a janela
      refetchOnMount: true, // Mantém refetch ao montar o componente
      refetchOnReconnect: true, // Mantém refetch ao reconectar
      staleTime: 5 * 60 * 1000, // Considera dados "frescos" por 5 minutos
      retry: (failureCount, error: any) => {
        // Não tentar novamente se for AbortError (requisição cancelada)
        if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
          return false;
        }
        // Tentar até 2 vezes para outros erros
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Não tentar novamente se for AbortError
        if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
          return false;
        }
        return failureCount < 1; // Apenas 1 tentativa para mutations
      },
    },
  },
});

// Componente simples de proteção de rota
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { role, loading } = useRole();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0f141c] pt-16">
        <div className="h-12 w-12 text-[#00B6F0] animate-spin">Loading...</div>
      </div>
    );
  }
  
  // Se não está logado, redireciona para login
  if (!role) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

const AppContent = () => {

  return (
    <HeroBackgroundWrapper>
      <ScrollToTop />
      <AppLayout>
        <MobileSidebar />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/mentores" element={<MentoresPage />} />
          <Route path="/comunidades" element={<ComunidadesPage />} />
          <Route path="/comunidades/:slug" element={<CommunityPage />} />
          <Route path="/jogos" element={<JogosPage />} />
          <Route path="/mentorias" element={<MentoriasPage />} />
          <Route path="/guias" element={<GuiasPage />} />
          <Route path="/mentores/:mentorId" element={<MentorPublicPage />} />
          <Route path="/como-funciona" element={<ComoFuncionaPage />} />
          
          {/* Páginas de Jogos Otimizadas para SEO */}
          <Route path="/como-jogar-valorant" element={<ComoJogarValorant />} />
          <Route path="/como-jogar-pokemon-tcg" element={<ComoJogarPokemonTCG />} />
          <Route path="/como-jogar-fortnite" element={<ComoJogarFortnite />} />
          
          {/* Páginas de Mentores por Jogo */}
          <Route path="/mentor-valorant" element={<MentorValorant />} />
          <Route path="/mentor-pokemon-tcg" element={<MentorPokemonTCG />} />
          <Route path="/mentor-fortnite" element={<MentorFortnite />} />
          <Route path="/cadastro" element={<AuthPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/recuperar-senha" element={<AuthPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-canceled" element={<PaymentCanceled />} />
          <Route path="/digital-content-success" element={<DigitalContentSuccess />} />
          
          {/* Páginas legais e políticas */}
          <Route path="/termos-de-uso" element={<TermsOfService />} />
          <Route path="/politica-privacidade" element={<PrivacyPolicy />} />
          <Route path="/guia-do-mentor" element={<MentorGuide />} />
          <Route path="/politicas-pagamento" element={<PaymentPolicies />} />
          
          {/* Redirecionamento da rota antiga /admin para a nova /painel-admin */}
          <Route path="/admin/*" element={<Navigate to="/painel-admin" replace />} />
          
          {/* Admin Panel Routes - usar wildcard para subrotas */}
          <Route path="/painel-admin/*" element={<AdminPanel />} />
          
          {/* Debug routes */}
          <Route path="/debug/nickname" element={<NicknameDebug />} />
          <Route path="/debug/comunidades" element={<CommunityDebug />} />
          <Route path="/debug/digital-content-purchases" element={<DigitalContentPurchaseDebug />} />
          
          {/* Mentor Panel Routes */}
          <Route path="/painel-mentor/*" element={<MentorPanel />} />
          
          {/* Player Panel Routes */}
          <Route path="/painel-jogador/*" element={<PlayerPanel />} />
          
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:conversationId" element={<ChatPage />} />
          
          {/* Debug route - accessible when logged in */}
          <Route path="/debug/email" element={
            <ProtectedRoute>
              <EmailDebugPage />
            </ProtectedRoute>
          } />
          
          {/* Template test route - accessible when logged in */}
          <Route path="/debug/templates" element={
            <ProtectedRoute>
              <EmailTemplateTestPage />
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AppLayout>



      {/* Sistema de consentimento de cookies */}
      <CookieConsent />
    </HeroBackgroundWrapper>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AppContent />
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
